import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, type FacultyMember } from "../api.js";
import { useAuth } from "../auth.js";

/**
 * College-admin console: add and deactivate faculty for one institution --
 * always the signed-in admin's own. Appointing teachers is the institution's
 * job, not the platform operator's, so there is no cross-tenant scope here.
 */
export default function Faculty() {
  const { user } = useAuth();

  const [faculty, setFaculty] = useState<FacultyMember[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .get<{ faculty: FacultyMember[] }>("/api/admin/faculty")
      .then((r) => setFaculty(r.faculty))
      .catch((e) => setError(e.message));

  useEffect(() => {
    void load();
  }, []);

  async function addFaculty(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      await api.post("/api/admin/faculty", form);
      setStatus(`${form.email} can now sign in as faculty.`);
      setForm({ name: "", email: "" });
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not add the faculty member.");
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(m: FacultyMember) {
    if (!confirm(`${m.active ? "Deactivate" : "Reactivate"} ${m.name}?`)) return;
    try {
      await api.patch(`/api/admin/users/${m.id}`, { active: !m.active });
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not update the account.");
    }
  }

  if (user && user.role !== "UNIVERSITY_ADMIN") {
    return (
      <div className="page">
        <div className="error-banner">
          This console is for an institution's own administrators.
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="between" style={{ marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Faculty</h1>
          <div className="muted">Teachers who can create courses, assignments and exams</div>
        </div>
        <div className="row">
          <Link to="/audit"><button>Audit log</button></Link>
          <Link to="/"><button>Institution</button></Link>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}
      {status && <div className="notice">{status}</div>}

      <div className="card">
        <div style={{ marginBottom: 8, color: "var(--muted)", fontSize: 12 }}>ADD FACULTY</div>
        <form onSubmit={addFaculty}>
          <div className="grid-2">
            <label>
              <span>Name</span>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Prof. Rao"
                required
              />
            </label>
            <label>
              <span>Email</span>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="rao@college.edu"
                required
              />
            </label>
          </div>
          <button className="primary" disabled={busy}>{busy ? "Adding…" : "Add faculty"}</button>
        </form>
      </div>

      <div className="card">
        <div className="between" style={{ marginBottom: 12 }}>
          <strong>Staff ({faculty.length})</strong>
        </div>
        {faculty.length === 0 ? (
          <div className="muted">No faculty yet.</div>
        ) : (
          <table>
            <thead>
              <tr><th>Name</th><th>Email</th><th>Role</th><th>Courses</th><th>Status</th><th /></tr>
            </thead>
            <tbody>
              {faculty.map((m) => (
                <tr key={m.id}>
                  <td>{m.name}</td>
                  <td className="muted">{m.email}</td>
                  <td>
                    <span className="badge draft">
                      {m.role === "UNIVERSITY_ADMIN" ? "admin" : "teacher"}
                    </span>
                  </td>
                  <td>{m._count?.createdCourses ?? 0}</td>
                  <td>
                    <span className={`badge ${m.active ? "pass" : "fail"}`}>
                      {m.active ? "active" : "inactive"}
                    </span>
                  </td>
                  <td>
                    {m.id !== user?.id && (
                      <button className={m.active ? "danger" : ""} onClick={() => toggleActive(m)}>
                        {m.active ? "Deactivate" : "Reactivate"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
